import { useMemo } from "react";

/**
 * Pure CSS/SVG futuristic 3D globe with animated location pins,
 * connecting arcs, and rotating wireframe. Reliable, lightweight,
 * works without any 3D library.
 */
export default function Globe3D({ className = "" }) {
  const pins = useMemo(
    () => [
      { x: 30, y: 35, color: "#F97316" },
      { x: 55, y: 28, color: "#22C55E" },
      { x: 70, y: 42, color: "#F97316" },
      { x: 42, y: 50, color: "#F97316" },
      { x: 38, y: 65, color: "#22C55E" },
      { x: 62, y: 70, color: "#F97316" },
      { x: 50, y: 78, color: "#EF4444" },
      { x: 80, y: 55, color: "#F97316" },
      { x: 22, y: 55, color: "#22C55E" },
      { x: 48, y: 22, color: "#F97316" },
    ],
    []
  );

  // Latitude rings (ellipses)
  const lats = [25, 40, 50, 60, 75];
  // Longitude meridians
  const lngs = [0, 22.5, 45, 67.5, 90, 112.5, 135, 157.5];

  return (
    <div
      data-testid="hero-globe-3d"
      className={`relative globe-3d ${className}`}
      style={{ perspective: 1000 }}
    >
      <style>{`
        @keyframes globe-spin { 0% { transform: rotateY(0); } 100% { transform: rotateY(360deg); } }
        @keyframes globe-pulse { 0%, 100% { transform: scale(1); opacity: 0.6; } 50% { transform: scale(1.4); opacity: 0; } }
        @keyframes arc-draw { 0% { stroke-dashoffset: 800; } 100% { stroke-dashoffset: 0; } }
        @keyframes float-yz { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
        .globe-shell { transform-style: preserve-3d; animation: globe-spin 40s linear infinite; }
        .globe-glow { filter: blur(2px); }
      `}</style>

      {/* outer aura */}
      <div className="absolute inset-0 rounded-full bg-[#FB923C]/15 blur-3xl" />

      <div
        className="relative mx-auto h-full w-full flex items-center justify-center"
        style={{ animation: "float-yz 7s ease-in-out infinite" }}
      >
        <div
          className="relative aspect-square h-full max-h-[520px] rounded-full"
          style={{
            background:
              "radial-gradient(circle at 35% 30%, rgba(249,115,22,0.35), rgba(0,0,0,0.95) 60%)",
            boxShadow:
              "inset -40px -40px 90px rgba(0,0,0,0.7), inset 40px 30px 80px rgba(249,115,22,0.25), 0 0 80px rgba(249,115,22,0.5)",
            border: "1px solid rgba(249,115,22,0.45)",
          }}
        >
          {/* Spinning wireframe */}
          <div className="absolute inset-2 rounded-full globe-shell">
            <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
              <defs>
                <radialGradient id="globeFade" cx="50%" cy="50%" r="50%">
                  <stop offset="60%" stopColor="rgba(249,115,22,0.5)" />
                  <stop offset="100%" stopColor="rgba(249,115,22,0)" />
                </radialGradient>
              </defs>
              {/* Latitude ellipses */}
              {lats.map((ry) => (
                <ellipse
                  key={`lat-${ry}`}
                  cx="50"
                  cy="50"
                  rx="48"
                  ry={(ry / 100) * 48}
                  fill="none"
                  stroke="rgba(249,115,22,0.4)"
                  strokeWidth="0.25"
                />
              ))}
              {/* Longitude ellipses (rotated by transform) */}
              {lngs.map((deg) => (
                <ellipse
                  key={`lng-${deg}`}
                  cx="50"
                  cy="50"
                  rx={48 * Math.abs(Math.cos((deg * Math.PI) / 180))}
                  ry="48"
                  fill="none"
                  stroke="rgba(249,115,22,0.4)"
                  strokeWidth="0.25"
                />
              ))}
              {/* Equator highlight */}
              <line x1="2" y1="50" x2="98" y2="50" stroke="rgba(249,115,22,0.5)" strokeWidth="0.3" strokeDasharray="2 2" />
            </svg>
          </div>

          {/* Pins layer (front-facing, not spinning) */}
          <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
            {/* Arcs connecting pins */}
            {pins.map((p, i) => {
              const b = pins[(i + 3) % pins.length];
              const midX = (p.x + b.x) / 2;
              const midY = (p.y + b.y) / 2 - 8;
              return (
                <path
                  key={`arc-${i}`}
                  d={`M ${p.x} ${p.y} Q ${midX} ${midY} ${b.x} ${b.y}`}
                  stroke="#F97316"
                  strokeWidth="0.25"
                  strokeOpacity="0.5"
                  fill="none"
                  strokeDasharray="800"
                  style={{ animation: `arc-draw ${4 + (i % 3)}s ease-in-out ${i * 0.2}s infinite alternate` }}
                />
              );
            })}
            {pins.map((p, i) => (
              <g key={`pin-${i}`}>
                <circle cx={p.x} cy={p.y} r="2" fill={p.color} opacity="0.3" style={{ animation: `globe-pulse ${2 + (i % 3)}s ease-out infinite` }} />
                <circle cx={p.x} cy={p.y} r="1" fill={p.color} />
              </g>
            ))}
          </svg>

          {/* Center hub */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-[#F97316] shadow-[0_0_18px_#F97316]" />
        </div>

        {/* Floating data chips */}
        <FloatingChip
          className="absolute -top-2 left-2 md:left-6"
          title="Atlanta · LOC-23914"
          subtitle="In Progress · Risk 78"
          dot="#F97316"
        />
        <FloatingChip
          className="absolute bottom-6 right-0 md:right-4"
          title="Nashville · LOC-23921"
          subtitle="Assigned · 5h SLA"
          dot="#22C55E"
        />
        <FloatingChip
          className="absolute top-1/3 -right-2 md:right-0"
          title="LIVE · 12 markets"
          subtitle="247 open tickets"
          dot="#FDBA74"
        />
      </div>
    </div>
  );
}

function FloatingChip({ className = "", title, subtitle, dot }) {
  return (
    <div className={`glass-strong rounded-xl px-3 py-2 border border-white/10 shadow-2xl text-left ${className}`} style={{ animation: "float-yz 5s ease-in-out infinite" }}>
      <div className="flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full pulse-dot" style={{ backgroundColor: dot }} />
        <span className="font-mono text-[10px] text-white tracking-wide">{title}</span>
      </div>
      <div className="text-[9px] text-[#94A3B8] mt-0.5 ml-3.5">{subtitle}</div>
    </div>
  );
}
